<template>
  <v-dialog v-model="dialogVisible" max-width="1200px" scrollable persistent>
    <v-card class="schedule-calendar-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-left">
          <v-icon size="24" color="white">mdi-calendar-clock</v-icon>
          <div class="header-title-section">
            <div class="employee-navigator">
              <button 
                class="emp-nav-btn" 
                @click="navigateEmployee(-1)"
                :disabled="!canNavigatePrev"
                title="이전 직원"
              >
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
              <div class="emp-selector-wrapper">
                <select v-model="currentEmployeeIndex" class="emp-select" @change="onEmployeeChange">
                  <option 
                    v-for="(emp, idx) in caregiverList" 
                    :key="`${emp.emp_name}-${emp.emp_birth}`" 
                    :value="idx"
                  >
                    {{ emp.emp_name }} ({{ emp.emp_job || '요양보호사' }})
                  </option>
                </select>
                <span class="emp-counter">{{ currentEmployeeIndex + 1 }} / {{ caregiverList.length }}</span>
              </div>
              <button 
                class="emp-nav-btn" 
                @click="navigateEmployee(1)"
                :disabled="!canNavigateNext"
                title="다음 직원"
              >
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>
        <div class="header-controls">
          <div class="year-month-selector">
            <button class="nav-btn" @click="changeMonth(-1)">
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <div class="current-period">
              <select v-model="selectedYear" class="year-select">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}년</option>
              </select>
              <select v-model="selectedMonth" class="month-select">
                <option v-for="m in 12" :key="m" :value="m">{{ m }}월</option>
              </select>
            </div>
            <button class="nav-btn" @click="changeMonth(1)">
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>
          <button class="pdf-btn" @click="printSchedulePdf" :disabled="isPdfLoading || scheduleData.length === 0" title="일정표 PDF 출력">
            <v-icon size="18" color="white">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
            <span>PDF</span>
          </button>
          <button class="close-btn" @click="close">
            <v-icon size="20" color="white">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- 뷰 선택 탭 -->
      <div class="view-tabs">
        <button 
          class="view-tab" 
          :class="{ active: activeView === 'caregiver' }"
          @click="activeView = 'caregiver'"
        >
          <v-icon size="18">mdi-account-hard-hat</v-icon>
          <span>{{ currentEmployee?.emp_name }} 근무일정</span>
        </button>
        <button 
          class="view-tab" 
          :class="{ active: activeView === 'patient' }"
          @click="activeView = 'patient'"
        >
          <v-icon size="18">mdi-account-heart</v-icon>
          <span>담당 수급자</span>
          <span class="count-badge">{{ uniquePatients.length }}</span>
        </button>
      </div>

      <!-- 컨텐츠 -->
      <div class="modal-content">
        <div v-if="isLoading" class="loading-state">
          <v-progress-circular indeterminate color="#0d47a1" size="40" />
          <span>일정을 불러오는 중...</span>
        </div>

        <div v-else-if="scheduleData.length === 0" class="empty-state">
          <v-icon size="56" color="#cbd5e1">mdi-calendar-blank</v-icon>
          <h3>일정 데이터가 없습니다</h3>
          <p>{{ selectedYear }}년 {{ selectedMonth }}월에 {{ currentEmployee?.emp_name }}님의 일정이 없습니다.</p>
          <LtcsMissingRequestCta
            :member-code="memberCode"
            :biz-gubun="bizGubun"
            :year-month="requestYearMonth"
            :types="SCHEDULE_LTCS_REQUEST_TYPES"
            @updated="fetchScheduleData"
          />
        </div>

        <template v-else>
          <!-- 종사자 근무일정 뷰 -->
          <div v-if="activeView === 'caregiver'" class="calendar-grid-container">
            <!-- 통계 바 -->
            <div class="caregiver-info-bar">
              <div class="caregiver-badge">
                <v-avatar size="36" color="#0d47a1">
                  <span class="avatar-text">{{ currentEmployee?.emp_name?.charAt(0) }}</span>
                </v-avatar>
                <div class="badge-info">
                  <span class="badge-name">{{ currentEmployee?.emp_name }}</span>
                  <span class="badge-job">{{ currentEmployee?.emp_job || '요양보호사' }}</span>
                </div>
              </div>
              <div class="quick-stats">
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.totalDays }}</span>
                  <span class="stat-label">근무일</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.totalHours }}</span>
                  <span class="stat-label">총 시간</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ monthlyStats.patientCount }}</span>
                  <span class="stat-label">담당 수급자</span>
                </div>
              </div>
            </div>

            <!-- 달력 -->
            <div class="calendar-wrapper">
              <div class="calendar-header">
                <div class="weekday" v-for="day in weekDays" :key="day" :class="{ sunday: day === '일', saturday: day === '토' }">{{ day }}</div>
              </div>
              <div class="calendar-body">
                <div 
                  v-for="(day, idx) in calendarDays" 
                  :key="idx"
                  class="calendar-day"
                  :class="{ 
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-schedule': getDaySchedules(day.date).length > 0
                  }"
                >
                  <div class="day-header">
                    <div class="day-number" :class="{ sunday: day.dayOfWeek === 0, saturday: day.dayOfWeek === 6 }">{{ day.day }}</div>
                    <span v-if="day.isToday" class="today-chip">TODAY</span>
                  </div>
                  <div v-if="day.isCurrentMonth" class="day-schedules">
                    <div 
                      v-for="(schedule, sIdx) in getDaySchedules(day.date).slice(0, 4)" 
                      :key="sIdx"
                      class="schedule-chip-inline"
                      :class="{ 'highlighted': hoveredPatient === schedule.clsp_recipient_name }"
                      :style="{ borderLeftColor: getPatientColor(schedule.clsp_recipient_name) }"
                      @mouseenter="hoveredPatient = schedule.clsp_recipient_name"
                      @mouseleave="hoveredPatient = null"
                    >
                      <span class="chip-text">{{ schedule.clsp_recipient_name }} {{ formatTime(schedule.clsp_start_time) }}-{{ formatTime(schedule.clsp_end_time) }}</span>
                    </div>
                    <div 
                      v-if="getDaySchedules(day.date).length > 4" 
                      class="more-schedules"
                    >
                      +{{ getDaySchedules(day.date).length - 4 }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 담당 수급자 목록 -->
            <div class="patient-list-card">
              <h4>
                <v-icon size="18" color="#0d47a1">mdi-account-heart</v-icon>
                담당 수급자 목록
              </h4>
              <div class="patient-chips">
                <div 
                  v-for="pt in patientList" 
                  :key="pt.name"
                  class="patient-chip"
                  :style="{ borderLeftColor: getPatientColor(pt.name) }"
                  @mouseenter="hoveredPatient = pt.name"
                  @mouseleave="hoveredPatient = null"
                  :class="{ 'highlighted': hoveredPatient === pt.name }"
                >
                  <div class="chip-color" :style="{ backgroundColor: getPatientColor(pt.name) }"></div>
                  <span class="chip-name">{{ pt.name }}</span>
                  <span class="chip-count">{{ pt.count }}회</span>
                  <span class="chip-hours">{{ pt.hours }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 담당 수급자별 뷰 -->
          <div v-if="activeView === 'patient'" class="calendar-grid-container">
            <!-- 수급자 정보 바 -->
            <div class="patient-info-bar">
              <div class="patient-selector-area">
                <label class="select-label">수급자 선택</label>
                <div class="select-wrapper">
                  <select v-model="selectedPatient" class="person-select">
                    <option v-for="patient in uniquePatients" :key="patient" :value="patient">
                      {{ patient }}
                    </option>
                  </select>
                  <span class="select-arrow">▼</span>
                </div>
              </div>
              <div class="patient-stats-row">
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientStats.totalDays }}</span>
                  <span class="stat-label">서비스일</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientStats.totalHours }}</span>
                  <span class="stat-label">총시간</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientStats.visitCount }}</span>
                  <span class="stat-label">방문</span>
                </div>
              </div>
            </div>

            <!-- 수급자별 달력 -->
            <div class="calendar-wrapper">
              <div class="calendar-header">
                <div class="weekday" v-for="day in weekDays" :key="day" :class="{ sunday: day === '일', saturday: day === '토' }">{{ day }}</div>
              </div>
              <div class="calendar-body">
                <div 
                  v-for="(day, idx) in calendarDays" 
                  :key="idx"
                  class="calendar-day"
                  :class="{ 
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-schedule': getPatientDaySchedules(day.date).length > 0
                  }"
                >
                  <div class="day-header">
                    <div class="day-number" :class="{ sunday: day.dayOfWeek === 0, saturday: day.dayOfWeek === 6 }">{{ day.day }}</div>
                    <span v-if="day.isToday" class="today-chip">TODAY</span>
                  </div>
                  <div v-if="day.isCurrentMonth" class="day-schedules">
                    <div 
                      v-for="(schedule, sIdx) in getPatientDaySchedules(day.date).slice(0, 3)" 
                      :key="sIdx"
                      class="schedule-chip caregiver"
                    >
                      <div class="chip-dot"></div>
                      <div class="chip-info">
                        <span class="chip-time">{{ formatTime(schedule.clsp_start_time) }}-{{ formatTime(schedule.clsp_end_time) }}</span>
                        <span class="chip-service">{{ schedule.clsp_fee_name || schedule.clsp_service_type }}</span>
                      </div>
                    </div>
                    <div 
                      v-if="getPatientDaySchedules(day.date).length > 3" 
                      class="more-schedules"
                    >
                      +{{ getPatientDaySchedules(day.date).length - 3 }}건
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { homeScheduleApi } from '@/services/api';
import { mapSchedulePlanRows, normalizeScheduleDate } from '@/utils/scheduleDate';
import { downloadPdfBlob, getApiErrorMessage } from '@/utils/downloadPdf';
import LtcsMissingRequestCta from '@/components/member/request/LtcsMissingRequestCta.vue';

const SCHEDULE_LTCS_REQUEST_TYPES = ['caion_ltcs_schedule_plan'] as const;

interface ScheduleData {
  clsp_idx: number;
  clsp_date: string;
  clsp_start_time: string;
  clsp_end_time: string;
  clsp_recipient_name: string;
  clsp_recipient_cert_number: string;
  clsp_caregiver_name: string;
  clsp_caregiver_birth: string;
  clsp_service_type: string;
  clsp_fee_name: string;
}

interface CalendarDay {
  day: number;
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  dayOfWeek: number;
}

interface Props {
  modelValue: boolean;
  caregiver: any;
  caregivers?: any[];
  memberCode: number | string;
  bizGubun?: string;
  year?: number;
  month?: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'change-caregiver', caregiver: any): void;
}>();

const currentEmployeeIndex = ref(0);
const activeView = ref<'caregiver' | 'patient'>('caregiver');
const hoveredPatient = ref<string | null>(null);
const selectedPatient = ref('');

const caregiverList = computed(() => {
  if (props.caregivers && props.caregivers.length > 0) {
    return props.caregivers;
  }
  return props.caregiver ? [props.caregiver] : [];
});

const currentEmployee = computed(() => {
  return caregiverList.value[currentEmployeeIndex.value] || props.caregiver;
});

const canNavigatePrev = computed(() => currentEmployeeIndex.value > 0);
const canNavigateNext = computed(() => currentEmployeeIndex.value < caregiverList.value.length - 1);

const navigateEmployee = (delta: number) => {
  const newIndex = currentEmployeeIndex.value + delta;
  if (newIndex >= 0 && newIndex < caregiverList.value.length) {
    currentEmployeeIndex.value = newIndex;
    emit('change-caregiver', currentEmployee.value);
    fetchScheduleData();
  }
};

const onEmployeeChange = () => {
  emit('change-caregiver', currentEmployee.value);
  fetchScheduleData();
};

const isLoading = ref(false);
const isPdfLoading = ref(false);
const scheduleData = ref<ScheduleData[]>([]);
/** 일정 조회에 실제 사용된 bizGubun (필터 재시도 후 PDF와 동일 조건 유지) */
const scheduleFetchBizGubun = ref<string | undefined>(undefined);

const today = new Date();
const selectedYear = ref(props.year || today.getFullYear());
const selectedMonth = ref(props.month || (today.getMonth() + 1));
const requestYearMonth = computed(
  () => `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`
);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
});

const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

const calendarDays = computed<CalendarDay[]>(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;

  const firstDay = new Date(year, month - 1, 1);
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());

  const days: CalendarDay[] = [];
  const current = new Date(startDay);
  const todayStr = formatDateStr(today);

  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === month - 1;
    const dateStr = formatDateStr(current);

    days.push({
      day: current.getDate(),
      date: dateStr,
      isCurrentMonth,
      isToday: dateStr === todayStr,
      dayOfWeek: current.getDay()
    });

    current.setDate(current.getDate() + 1);
  }

  return days;
});

const uniquePatients = computed(() => {
  const patients = new Set(scheduleData.value.map(s => s.clsp_recipient_name));
  return Array.from(patients).filter(Boolean).sort();
});

const monthlyStats = computed(() => {
  const uniqueDays = new Set(scheduleData.value.map(s => s.clsp_date));
  const uniquePatientsSet = new Set(scheduleData.value.map(s => s.clsp_recipient_name));

  let totalMinutes = 0;
  scheduleData.value.forEach(s => {
    totalMinutes += calculateMinutes(s.clsp_start_time, s.clsp_end_time);
  });

  return {
    totalDays: uniqueDays.size,
    totalHours: formatHours(totalMinutes),
    patientCount: uniquePatientsSet.size
  };
});

const getPatientStats = computed(() => {
  if (!selectedPatient.value) return { totalDays: 0, totalHours: '0h', visitCount: 0 };
  
  const patientSchedules = scheduleData.value.filter(s => s.clsp_recipient_name === selectedPatient.value);
  const uniqueDays = new Set(patientSchedules.map(s => s.clsp_date));
  
  let totalMinutes = 0;
  patientSchedules.forEach(s => {
    totalMinutes += calculateMinutes(s.clsp_start_time, s.clsp_end_time);
  });
  
  return {
    totalDays: uniqueDays.size,
    totalHours: formatHours(totalMinutes),
    visitCount: patientSchedules.length
  };
});

const patientList = computed(() => {
  const ptData: Record<string, { count: number; minutes: number }> = {};

  scheduleData.value.forEach(s => {
    if (!ptData[s.clsp_recipient_name]) {
      ptData[s.clsp_recipient_name] = { count: 0, minutes: 0 };
    }
    ptData[s.clsp_recipient_name].count++;
    ptData[s.clsp_recipient_name].minutes += calculateMinutes(s.clsp_start_time, s.clsp_end_time);
  });

  return Object.entries(ptData)
    .map(([name, data]) => ({
      name,
      count: data.count,
      hours: formatHours(data.minutes)
    }))
    .sort((a, b) => b.count - a.count);
});

const patientColors: Record<string, string> = {};
const colorPalette = [
  '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316',
  '#14b8a6', '#ec4899', '#4f46e5', '#eab308', '#64748b'
];

const getPatientColor = (name: string): string => {
  if (!patientColors[name]) {
    const idx = Object.keys(patientColors).length % colorPalette.length;
    patientColors[name] = colorPalette[idx];
  }
  return patientColors[name];
};

const formatDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime = (time: string | undefined): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  }
  return time;
};

const calculateMinutes = (startTime: string, endTime: string): number => {
  const start = formatTime(startTime).split(':');
  const end = formatTime(endTime).split(':');
  if (start.length < 2 || end.length < 2) return 0;

  const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
  const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
  return Math.max(0, endMinutes - startMinutes);
};

const formatHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins > 0) return `${hours}h ${mins}m`;
  return `${hours}h`;
};

const getDaySchedules = (dateStr: string): ScheduleData[] => {
  return scheduleData.value.filter((s) => normalizeScheduleDate(s.clsp_date) === dateStr);
};

const getPatientDaySchedules = (dateStr: string): ScheduleData[] => {
  if (!selectedPatient.value) return [];
  return scheduleData.value.filter(
    (s) =>
      normalizeScheduleDate(s.clsp_date) === dateStr &&
      s.clsp_recipient_name === selectedPatient.value
  );
};

const changeMonth = (delta: number) => {
  let newMonth = selectedMonth.value + delta;
  let newYear = selectedYear.value;

  if (newMonth > 12) {
    newMonth = 1;
    newYear++;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear--;
  }

  selectedMonth.value = newMonth;
  selectedYear.value = newYear;
  fetchScheduleData();
};

const fetchScheduleData = async () => {
  const emp = currentEmployee.value;
  if (!props.memberCode || !emp?.emp_name) return;

  const empBirth = emp.emp_birth ? String(emp.emp_birth).split('T')[0] : '';
  if (!empBirth) {
    scheduleData.value = [];
    return;
  }

  try {
    isLoading.value = true;
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;

    let effectiveBizGubun: string | undefined = props.bizGubun || undefined;
    let res = await homeScheduleApi.getCaregiverSchedule(
      props.memberCode,
      emp.emp_name,
      yearMonth,
      effectiveBizGubun,
      empBirth
    );

    if (
      res.data.success &&
      (!res.data.data || res.data.data.length === 0) &&
      props.bizGubun
    ) {
      effectiveBizGubun = undefined;
      res = await homeScheduleApi.getCaregiverSchedule(
        props.memberCode,
        emp.emp_name,
        yearMonth,
        undefined,
        empBirth
      );
    }

    scheduleFetchBizGubun.value = effectiveBizGubun;

    if (res.data.success) {
      scheduleData.value = mapSchedulePlanRows(res.data.data || []);
      if (uniquePatients.value.length > 0 && !uniquePatients.value.includes(selectedPatient.value)) {
        selectedPatient.value = uniquePatients.value[0];
      }
    } else {
      scheduleData.value = [];
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '일정 조회에 실패했습니다.';
    const status = error.response?.status || '';
    console.error('일정 데이터 조회 오류:', error);
    alert(`일정 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    scheduleData.value = [];
  } finally {
    isLoading.value = false;
  }
};

const printSchedulePdf = async () => {
  const emp = currentEmployee.value;
  if (!props.memberCode || !emp?.emp_name) return;

  const empBirth = emp.emp_birth ? String(emp.emp_birth).split('T')[0] : '';
  if (!empBirth) {
    alert('생년월일이 있어야 일정표 PDF를 출력할 수 있습니다.');
    return;
  }

  try {
    isPdfLoading.value = true;
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;

    const bizForPdf = scheduleFetchBizGubun.value;
    let res = await homeScheduleApi.downloadCaregiverPdf(
      props.memberCode,
      emp.emp_name,
      yearMonth,
      bizForPdf,
      empBirth
    );

    downloadPdfBlob(res.data, `일정표_${emp.emp_name}_${yearMonth}.pdf`);
  } catch (error: unknown) {
    const message = await getApiErrorMessage(error, '일정표 PDF 출력에 실패했습니다.');
    console.error('PDF 출력 오류:', error);
    alert(`일정표 PDF 출력 실패: ${message}`);
  } finally {
    isPdfLoading.value = false;
  }
};

const close = () => {
  dialogVisible.value = false;
};

watch(dialogVisible, async (visible) => {
  if (visible) {
    selectedYear.value = props.year || today.getFullYear();
    selectedMonth.value = props.month || (today.getMonth() + 1);
    activeView.value = 'caregiver';
    selectedPatient.value = '';
    scheduleFetchBizGubun.value = undefined;

    if (props.caregivers && props.caregivers.length > 0 && props.caregiver) {
      const idx = props.caregivers.findIndex(e =>
        e.emp_name === props.caregiver.emp_name && e.emp_birth === props.caregiver.emp_birth
      );
      currentEmployeeIndex.value = idx >= 0 ? idx : 0;
    } else {
      currentEmployeeIndex.value = 0;
    }

    await nextTick();
    if (currentEmployee.value?.emp_name) {
      await fetchScheduleData();
    }
  }
});

watch([selectedYear, selectedMonth], async ([newYear, newMonth], [oldYear, oldMonth]) => {
  if (dialogVisible.value && (newYear !== oldYear || newMonth !== oldMonth)) {
    await fetchScheduleData();
  }
});

watch(uniquePatients, (patients) => {
  if (patients.length > 0 && !patients.includes(selectedPatient.value)) {
    selectedPatient.value = patients[0];
  }
});
</script>

<style lang="scss" scoped>
.schedule-calendar-modal {
  overflow: hidden;
  border-radius: 8px !important;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #1e3a5f;
  color: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-navigator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.emp-nav-btn {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.emp-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.emp-select {
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  min-width: 180px;

  option {
    color: #1e293b;
    background: #fff;
  }
}

.emp-counter {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.year-month-selector {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-btn {
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.current-period {
  display: flex;
  align-items: center;
  gap: 2px;

  .year-select,
  .month-select {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    text-align: center;

    option {
      background: #1e3a5f;
      color: #fff;
    }
  }

  .year-select {
    width: 70px;
  }

  .month-select {
    width: 50px;
  }
}

.pdf-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #c62828;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #b71c1c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.view-tabs {
  display: flex;
  gap: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  .view-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: transparent;
      transition: all 0.15s;
    }

    &:hover {
      color: #64748b;
    }

    &.active {
      color: #1e3a5f;

      &::after {
        background: #1e3a5f;
      }
    }

    .count-badge {
      background: #e2e8f0;
      color: #64748b;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
    }

    &.active .count-badge {
      background: #1e3a5f;
      color: #fff;
    }
  }
}

.modal-content {
  flex: 1;
  padding: 20px;
  height: 70vh;
  min-height: 70vh;
  max-height: 70vh;
  overflow-y: auto;
  background: #fafafa;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 500px;
  gap: 12px;
  color: #64748b;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.calendar-grid-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.caregiver-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #1e3a5f;
  padding: 14px 20px;
  border-radius: 4px;

  .caregiver-badge {
    display: flex;
    align-items: center;
    gap: 12px;

    .avatar-text {
      color: #fff;
      font-weight: 600;
      font-size: 14px;
    }

    .badge-info {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .badge-name {
        color: #1e293b;
        font-size: 15px;
        font-weight: 600;
      }

      .badge-job {
        color: #64748b;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }

  .quick-stats {
    display: flex;
    gap: 24px;

    .stat {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      .stat-value {
        color: #1e3a5f;
        font-size: 20px;
        font-weight: 700;
        line-height: 1;
      }

      .stat-label {
        color: #94a3b8;
        font-size: 11px;
        font-weight: 500;
        margin-top: 4px;
      }
    }
  }
}

.patient-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #1e3a5f;
  padding: 12px 16px;
  border-radius: 4px;
  gap: 16px;
  flex-wrap: wrap;

  .patient-selector-area {
    display: flex;
    align-items: center;
    gap: 10px;

    .select-label {
      font-size: 12px;
      font-weight: 500;
      color: #64748b;
    }

    .select-wrapper {
      position: relative;
      display: inline-flex;
      align-items: center;

      .person-select {
        padding: 8px 32px 8px 12px;
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        cursor: pointer;
        min-width: 140px;
        appearance: none;
        transition: all 0.15s;

        &:hover {
          background: #e2e8f0;
          border-color: #94a3b8;
        }

        &:focus {
          outline: none;
          border-color: #1e3a5f;
        }
      }

      .select-arrow {
        position: absolute;
        right: 10px;
        font-size: 10px;
        color: #64748b;
        pointer-events: none;
      }
    }
  }

  .patient-stats-row {
    display: flex;
    align-items: center;
    gap: 20px;

    .stat-box {
      display: flex;
      flex-direction: column;
      align-items: center;

      .stat-value {
        font-size: 16px;
        font-weight: 700;
        color: #1e3a5f;
        line-height: 1;
      }

      .stat-label {
        font-size: 10px;
        font-weight: 500;
        color: #94a3b8;
        margin-top: 2px;
      }
    }
  }
}

.calendar-wrapper {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #1e3a5f;
  border-bottom: 1px solid #e2e8f0;

  .weekday {
    padding: 12px 4px;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: #fff;

    &.sunday {
      color: #fca5a5;
    }

    &.saturday {
      color: #93c5fd;
    }
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
  min-height: 90px;
  border-right: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  padding: 4px 6px;
  background: #fff;
  transition: background 0.1s;

  &:nth-child(7n) {
    border-right: none;
  }

  &:hover {
    background: #fafafa;
  }

  &.other-month {
    background: #f8f8f8;

    .day-number,
    .day-header {
      visibility: hidden;
    }

    .day-schedules {
      display: none;
    }
  }

  &.today {
    background: #fffbeb;
    border: 2px solid #f59e0b;

    .day-number {
      color: #d97706;
      font-weight: 700;
    }
  }

  &.has-schedule {
    background: #fff;
    border: 1px solid #94a3b8;
  }
}

.day-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.day-number {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;

  &.sunday {
    color: #dc2626;
  }

  &.saturday {
    color: #2563eb;
  }
}

.today-chip {
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  background: #f59e0b;
  padding: 1px 4px;
  border-radius: 2px;
}

.day-schedules {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.schedule-chip-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 2px;
  background: #fff;
  border-left: 4px solid;
  cursor: pointer;
  transition: all 0.15s;

  .chip-text {
    font-size: 10px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.highlighted {
    background: #e2e8f0;
    transform: scale(1.02);
    z-index: 1;
    position: relative;
    border-left-width: 5px;

    .chip-text {
      font-weight: 700;
    }
  }
}

.schedule-chip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 3px 6px;
  border-radius: 2px;
  background: #f8fafc;
  overflow: hidden;

  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 4px;
    background: #64748b;
  }

  &.caregiver .chip-dot {
    background: #1e3a5f;
  }

  .chip-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .chip-time {
    color: #1e293b;
    font-size: 10px;
    font-weight: 700;
  }

  .chip-service {
    color: #64748b;
    font-size: 9px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.more-schedules {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  padding: 2px 4px;
  background: #f1f5f9;
  border-radius: 2px;
  text-align: center;
}

.patient-list-card {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  border: 1px solid #e2e8f0;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .patient-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .patient-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid;
    border-radius: 0 4px 4px 0;
    transition: all 0.15s;
    cursor: pointer;

    &:hover,
    &.highlighted {
      background: #f1f5f9;
    }

    .chip-color {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .chip-name {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
    }

    .chip-count {
      font-size: 11px;
      font-weight: 600;
      color: #1e3a5f;
      background: #e2e8f0;
      padding: 2px 8px;
      border-radius: 10px;
    }

    .chip-hours {
      font-size: 11px;
      font-weight: 500;
      color: #64748b;
    }
  }
}

@media (max-width: 900px) {
  .modal-header {
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;

    .header-controls {
      width: 100%;
      justify-content: space-between;
    }
  }

  .caregiver-info-bar,
  .patient-info-bar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    .quick-stats,
    .patient-stats-row {
      width: 100%;
      justify-content: space-between;
    }
  }

  .calendar-day {
    min-height: 70px;
    padding: 3px;

    .schedule-chip-inline,
    .schedule-chip {
      padding: 2px 4px;

      .chip-text,
      .chip-time {
        font-size: 9px;
      }

      .chip-service {
        display: none;
      }
    }
  }
}
</style>
